import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, FileText, Lock, AlertTriangle, BookOpen, CheckCircle, ArrowRight } from 'lucide-react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { complianceAreas } from '../utils/mockData';

const Compliance = () => {
  const iconMap = {
    CheckCircle: CheckCircle,
    Shield: Shield,
    FileText: FileText,
    Lock: Lock,
    AlertTriangle: AlertTriangle,
    BookOpen: BookOpen
  };

  const standards = [
    'CQC Fundamental Standards of Quality and Safety',
    'Mental Health Act 1983 (amended 2007)',
    'Mental Capacity Act 2005',
    'Care Act 2014',
    'Health and Social Care Act 2008',
    'GDPR and Data Protection Act 2018'
  ];

  const governanceProcesses = [
    {
      title: 'Regular Audits',
      description: 'Internal audits of care quality, documentation, and compliance with policies.'
    },
    {
      title: 'Incident Management',
      description: 'Systematic reporting, investigation, and learning from incidents and near-misses.'
    },
    {
      title: 'Clinical Supervision',
      description: 'Regular supervision for all staff with qualified supervisors and reflective practice.'
    },
    {
      title: 'Quality Monitoring',
      description: 'Continuous monitoring of service quality, outcomes, and user feedback.'
    }
  ];

  return (
    <div className="min-h-screen pt-20">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7] py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <div className="flex justify-center mb-6">
              <Shield className="w-16 h-16 text-[#4A90E2]" />
            </div>
            <h1 className="heading-hero">Compliance & Governance</h1>
            <p className="body-large">
              Committed to the highest standards of safety, quality, and regulatory compliance.
            </p>
          </div>
        </div>
      </section>

      {/* Overview */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h2 className="heading-2 mb-4">Our Commitment to Excellence</h2>
            <p className="body-medium text-gray-700">
              Three Spheres Healthcare is committed to delivering safe, high-quality care that meets and exceeds regulatory requirements. As a mental health-led organisation, we place strong emphasis on clinical governance, safeguarding, and continuous quality improvement.
            </p>
            <p className="body-medium text-gray-700">
              We are working toward CQC registration and operate in line with all relevant healthcare regulations and best practice standards.
            </p>
          </div>
        </div>
      </section>

      {/* Compliance Areas */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="heading-2 mb-4">Our Compliance Framework</h2>
            <p className="body-large max-w-2xl mx-auto">
              Comprehensive policies and procedures across all areas of care delivery.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {complianceAreas.map((area, index) => {
              const Icon = iconMap[area.icon] || Shield;
              return (
                <Card key={index} className="p-6 border-0 hover:shadow-xl transition-all">
                  <div className="w-12 h-12 bg-gradient-to-br from-[#4A90E2] to-[#7B68BE] rounded-lg flex items-center justify-center mb-4">
                    <Icon className="text-white" size={24} />
                  </div>
                  <h3 className="heading-3 mb-3">{area.title}</h3>
                  <p className="text-sm text-gray-600">{area.description}</p>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Regulatory Standards */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 mb-8 text-center">Regulatory Standards</h2>
            <p className="body-medium text-center text-gray-700 mb-8">
              We operate in accordance with the following legislation and regulatory frameworks:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {standards.map((standard, index) => (
                <div key={index} className="flex items-start gap-3 bg-[#E8F4FC] rounded-lg p-4">
                  <CheckCircle className="text-[#4A90E2] flex-shrink-0 mt-1" size={20} />
                  <span className="text-gray-800">{standard}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Clinical Governance */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="heading-2 mb-8 text-center">Clinical Governance Processes</h2>
            <div className="grid md:grid-cols-2 gap-6">
              {governanceProcesses.map((process, index) => (
                <div key={index} className="bg-white rounded-xl p-6 shadow-sm">
                  <h3 className="font-semibold text-lg mb-3 text-gray-900">{process.title}</h3>
                  <p className="text-sm text-gray-600">{process.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Safeguarding */}
      <section className="section bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-[#E8F4FC] to-[#F0EDF7] rounded-2xl p-8 md:p-12">
              <div className="flex justify-center mb-6">
                <Shield className="w-12 h-12 text-[#4A90E2]" />
              </div>
              <h2 className="heading-2 mb-6 text-center">Safeguarding First</h2>
              <p className="body-medium text-center text-gray-700 mb-6">
                Safeguarding is at the heart of everything we do. We are committed to protecting vulnerable adults from abuse, harm, and neglect.
              </p>
              <div className="grid md:grid-cols-3 gap-4 mt-8">
                <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 text-center">
                  <CheckCircle className="w-8 h-8 text-[#4A90E2] mx-auto mb-3" />
                  <h4 className="font-semibold mb-2">Enhanced DBS Checks</h4>
                  <p className="text-sm text-gray-700">All staff undergo enhanced DBS screening</p>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 text-center">
                  <BookOpen className="w-8 h-8 text-[#4A90E2] mx-auto mb-3" />
                  <h4 className="font-semibold mb-2">Safeguarding Training</h4>
                  <p className="text-sm text-gray-700">Mandatory training for all team members</p>
                </div>
                <div className="bg-white/80 backdrop-blur-sm rounded-lg p-6 text-center">
                  <AlertTriangle className="w-8 h-8 text-[#4A90E2] mx-auto mb-3" />
                  <h4 className="font-semibold mb-2">Clear Reporting</h4>
                  <p className="text-sm text-gray-700">Robust policies and incident reporting</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Staff Training */}
      <section className="section bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="heading-2 mb-8 text-center">Staff Training & Development</h2>
            <p className="body-medium text-center text-gray-700 mb-8">
              All staff receive comprehensive training to ensure they have the knowledge, skills, and confidence to deliver high-quality care:
            </p>
            <div className="grid md:grid-cols-2 gap-6">
              <Card className="p-6 border-0">
                <h3 className="font-semibold text-lg mb-4 text-gray-900">Mandatory Training</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• Safeguarding adults</li>
                  <li>• Mental Health Act and Mental Capacity Act</li>
                  <li>• Health and safety</li>
                  <li>• Infection prevention and control</li>
                  <li>• Medication management</li>
                  <li>• Fire safety and first aid</li>
                </ul>
              </Card>
              <Card className="p-6 border-0">
                <h3 className="font-semibold text-lg mb-4 text-gray-900">Ongoing Development</h3>
                <ul className="space-y-2 text-sm text-gray-700">
                  <li>• Clinical supervision</li>
                  <li>• Reflective practice sessions</li>
                  <li>• Continuous professional development</li>
                  <li>• Specialist training in mental health</li>
                  <li>• Leadership and management development</li>
                  <li>• Learning from incidents</li>
                </ul>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section bg-gradient-to-r from-[#4A90E2] to-[#7B68BE] text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center space-y-6">
            <h2 className="heading-2 text-white">Questions About Our Standards?</h2>
            <p className="body-large text-white/90">
              We're happy to discuss our compliance framework, policies, and quality assurance processes.
            </p>
            <Link to="/contact">
              <Button size="lg" className="bg-white text-[#4A90E2] hover:bg-gray-100">
                Get in Touch
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Compliance;
